// hooks/queries/usePaginatedUsers.ts
import { QueryClient, useQuery, useQueryClient } from "react-query";
import { DocumentSnapshot } from "firebase/firestore";
import { firestore } from "@/services/firestore";
import { User } from "@/types/v2";
import { useTotalUsersQuery } from "./useTotalUsersQuery";
import { DatatableUser } from "@/components/Users/Users.columns";

interface UsePaginatedUsersParams {
  currentPage: number;
  pageSize: number;
  sortBy: string | null;
  sortOrder?: "asc" | "desc";
  enabled?: boolean;
  searchTerm?: string;
}

interface PaginatedUsersResult {
  docs: User[];
  total: number;
  hasMore: boolean;
  currentPage: number;
  totalPages: number;
}

// Store cursor snapshots in a Map for efficient lookups
const cursorCache = new Map<string, DocumentSnapshot>();

export const usePaginatedUsers = ({
  currentPage,
  pageSize,
  sortBy = "createdAt",
  sortOrder = "desc",
  enabled = true,
  searchTerm = "",
}: UsePaginatedUsersParams) => {
  const queryClient = useQueryClient();

  // Use the total users query
  const { data: totalUsers = 0, isLoading: isTotalLoading } =
    useTotalUsersQuery();

  const hasSearch = searchTerm && searchTerm.trim().length > 0;

  return useQuery({
    queryKey: [
      "paginated-users",
      currentPage,
      pageSize,
      sortBy,
      sortOrder,
      searchTerm,
    ],
    queryFn: async (): Promise<PaginatedUsersResult> => {
      // If we have a search term, use the filtered pagination
      if (hasSearch) {
        const trimmedSearch = searchTerm.trim();

        // Build search filters for multiple fields
        // Firestore doesn't support OR queries across different fields efficiently,
        // so we'll search for firstName, lastName, displayName, and email
        // Note: For case-insensitive search, we'd need to store lowercase versions
        // For now, we'll use array-contains or >= operators
        const searchOptions = [
          {
            searchParam: "email",
            opsString: ">=",
            equality: trimmedSearch,
          },
          {
            searchParam: "email",
            opsString: "<=",
            equality: trimmedSearch + "\uf8ff",
          },
        ];

        const result = await firestore.paginateDocumentsWithFilters<User>(
          "users",
          pageSize,
          undefined, // No cursor for search results (simpler implementation)
          undefined, // converter
          sortBy ?? "createdAt",
          sortOrder,
          searchOptions as any
        );

        // For search, we don't have an accurate total count without an additional query
        // We'll estimate based on results
        const estimatedTotal = result.totalFetched;

        return {
          docs: result.docs as DatatableUser[],
          total: estimatedTotal,
          hasMore: result.hasMore,
          currentPage,
          totalPages: Math.max(1, Math.ceil(estimatedTotal / pageSize)),
        };
      }

      // Regular pagination without search
      const prevPageKey = `paginated-users-${currentPage - 1}-${pageSize}-${sortBy}-${sortOrder}`;
      const currentPageKey = `paginated-users-${currentPage}-${pageSize}-${sortBy}-${sortOrder}`;

      // Get cursor from previous page if not first page
      let startAfterDoc: DocumentSnapshot | undefined;
      if (currentPage > 1) {
        startAfterDoc = cursorCache.get(prevPageKey);

        // If cursor not in cache, we need to fetch previous pages first
        if (!startAfterDoc) {
          await buildCursorChain(
            currentPage,
            pageSize,
            sortBy ?? "",
            sortOrder
          );
          startAfterDoc = cursorCache.get(prevPageKey);
        }
      }

      // Fetch current page - no need to request total count from Firestore
      const result = await firestore.paginateDocuments<User>(
        "users",
        pageSize,
        startAfterDoc,
        undefined, // converter
        sortBy ?? undefined,
        sortOrder
      );

      // Cache the cursor for this page
      if (result.lastVisible) {
        cursorCache.set(currentPageKey, result.lastVisible);
      }

      const totalPages = Math.ceil(totalUsers / pageSize);

      return {
        docs: result.docs.map((user) => ({
          ...user,
          // Add any additional fields needed for DatatableUser
        })) as DatatableUser[],
        total: totalUsers, // Use total from useTotalUsersQuery
        hasMore: result.hasMore,
        currentPage,
        totalPages,
      };
    },
    enabled: enabled && !isTotalLoading, // Wait for total to load first
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    keepPreviousData: true,
    // Prefetch next page (only when not searching)
    onSuccess: (data) => {
      if (data.hasMore && !hasSearch) {
        queryClient.prefetchQuery({
          queryKey: [
            "paginated-users",
            currentPage + 1,
            pageSize,
            sortBy,
            sortOrder,
            searchTerm,
          ],
          queryFn: async () => {
            const nextPageKey = `paginated-users-${currentPage}-${pageSize}-${sortBy}-${sortOrder}`;
            const startAfterDoc = cursorCache.get(nextPageKey);

            if (!startAfterDoc) return null;

            const result = await firestore.paginateDocuments<User>(
              "users",
              pageSize,
              startAfterDoc,
              undefined,
              sortBy ?? undefined,
              sortOrder
            );

            return {
              docs: result.docs as DatatableUser[],
              total: totalUsers, // Use cached total
              hasMore: result.hasMore,
              currentPage: currentPage + 1,
              totalPages: data.totalPages,
            };
          },
          staleTime: 5 * 60 * 1000,
        });
      }
    },
  });
};

// Helper function to build cursor chain for jumping to arbitrary pages
/**
 *
 * @param targetPage
 * @param pageSize
 * @param sortBy
 * @param sortOrder
 */
async function buildCursorChain(
  targetPage: number,
  pageSize: number,
  sortBy: string,
  sortOrder: "asc" | "desc"
) {
  let lastDoc: DocumentSnapshot | undefined;

  for (let page = 1; page < targetPage; page++) {
    const cacheKey = `paginated-users-${page}-${pageSize}-${sortBy}-${sortOrder}`;

    if (!cursorCache.has(cacheKey)) {
      const result = await firestore.paginateDocuments<User>(
        "users",
        pageSize,
        lastDoc,
        undefined,
        sortBy,
        sortOrder
      );

      if (result.lastVisible) {
        cursorCache.set(cacheKey, result.lastVisible);
        lastDoc = result.lastVisible;
      }
    } else {
      lastDoc = cursorCache.get(cacheKey);
    }
  }
}

// Invalidate cache when users are modified
const invalidateUsersCache = (queryClient: QueryClient) => {
  queryClient.invalidateQueries(["paginated-users"]);
  cursorCache.clear(); // Clear cursor cache when data changes
};

// Use this after creating, updating, or deleting users
const onUserUpdate = async (
  queryClient: QueryClient,
  userId: string,
  updates: Partial<User>
) => {
  // Optimistic update
  queryClient.setQueriesData(
    ["paginated-users"],
    (oldData: PaginatedUsersResult | undefined) => {
      if (!oldData)
        return {
          docs: [],
          total: 0,
          hasMore: false,
          currentPage: 1,
          totalPages: 1,
        };
      return {
        ...oldData,
        docs: oldData.docs.map((user) =>
          user.id === userId ? { ...user, ...updates } : user
        ),
      };
    }
  );

  // Then invalidate to refetch from server
  invalidateUsersCache(queryClient);
};

export const useInvalidateUsersCache = () => {
  const queryClient = useQueryClient();
  return () => invalidateUsersCache(queryClient);
};

export const useOnUserUpdate = () => {
  const queryClient = useQueryClient();
  return (userId: string, updates: Partial<User>) =>
    onUserUpdate(queryClient, userId, updates);
};
